<div class="page-container">
  <h2>Privacy Policy</h2>
  <div class="tos-date"><strong>Last Updated:</strong> December 14, 2025</div>
  <div class="privacy-container">
    <h2>1. Introduction</h2>
    <p>Vertical Talent, LLC ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website at <a href="https://verticaltalentgroup.com">verticaltalentgroup.com</a> (the "Site") or use our mobile application (the "App").</p>

    <h2>2. Who This Policy Applies To</h2>
    <p>This Privacy Policy applies to three distinct groups:</p>
    <ul>
        <li><strong>Public Website Visitors:</strong> Anyone who visits the public pages of our Site (Home, About Us, Privacy Policy, Terms of Use, and Contact Us).</li>
        <li><strong>Contact Form Users:</strong> Individuals who voluntarily submit information through our Contact Us form.</li>
        <li><strong>Represented Creators:</strong> Social media influencers and content creators who are represented by Vertical Talent and use our App and platform services.</li>
    </ul>

    <h2>3. Information We Collect</h2>

    <h3>3.1 From Public Website Visitors</h3>
    <p>We do not collect any personal information, use cookies, or employ tracking technologies when you browse the public pages of our Site.</p>

    <h3>3.2 From Contact Form Submissions</h3>
    <p>If you choose to contact us through our Contact Us form, you may voluntarily provide:</p>
    <ul>
        <li>Name</li>
        <li>Email address</li>
        <li>Social media handle</li>
        <li>Message content</li>
    </ul>
    <p>This information is sent directly to our email (info&#64;verticaltalentgroup.com) and is not stored in our database.</p>

    <h3>3.3 From Represented Creators</h3>
    <p>If you are a creator we represent, we collect the following information:</p>
    <ul>
        <li><strong>Account Information:</strong> Name, email address (&#64;verticaltalent.co), and Google account credentials used for authentication.</li>
        <li><strong>Contact Information:</strong> Phone number, physical mailing address (for product gifting and correspondence).</li>
        <li><strong>Tax Information:</strong> Social Security Number (SSN) or Employer Identification Number (EIN) collected via Google Form for W-9 purposes and 1099 tax reporting.</li>
        <li><strong>Social Media Platform Data:</strong> With your explicit OAuth authorization, we collect data from your TikTok, Instagram, and YouTube accounts, including:
            <ul>
                <li>Account handles and profile information</li>
                <li>Follower counts and audience demographics</li>
                <li>Video performance analytics (views, engagement rates, etc.)</li>
                <li>Content performance metrics</li>
            </ul>
        </li>
        <li><strong>Campaign Information:</strong> Data related to brand campaigns you participate in, including deliverables, compensation, contracts, communication records, and performance results.</li>
        <li><strong>Files and Content:</strong> Contracts, creative concepts, video assets, and other files uploaded or shared through our platform.</li>
    </ul>

    <h2>4. How We Use Your Information</h2>

    <h3>4.1 Contact Form Submissions</h3>
    <p>We use contact form information solely to respond to your inquiry.</p>

    <h3>4.2 Represented Creators</h3>
    <p>We use creator information to:</p>
    <ul>
        <li>Manage your representation and facilitate brand partnerships.</li>
        <li>Negotiate contracts and campaign terms on your behalf.</li>
        <li>Process payments and fulfill tax reporting obligations (1099 filings).</li>
        <li>Create media kits and promotional materials showcasing your work.</li>
        <li>Provide campaign performance reports to brand partners.</li>
        <li>Communicate with you about campaigns, deliverables, and opportunities.</li>
        <li>Store and share campaign-related files and content.</li>
        <li>Improve our services and platform functionality.</li>
    </ul>

    <h2>5. Mobile Application (iPhone App)</h2>
    <p>Our iPhone App is available exclusively to creators we represent. The App requires:</p>
    <ul>
        <li><strong>Google Authentication:</strong> You must sign in using your &#64;verticaltalent.co Google account.</li>
        <li><strong>Push Notifications:</strong> To alert you about campaign updates, messages, and important deadlines. You can disable notifications in your device settings.</li>
        <li><strong>Photo Library Access:</strong> To enable you to upload video assets and deliverables directly from your device. This permission is optional and only used when you choose to upload content.</li>
    </ul>
    <p>The App primarily provides read-only access to campaign information and allows you to upload content related to your campaigns. All data accessed through the App is stored on our secure servers, not on your device.</p>

    <h2>6. How We Share Your Information</h2>
    <p>We do not sell your personal information. We share information only in the following circumstances:</p>

    <h3>6.1 With Brand Partners</h3>
    <p>For creators we represent, we share the following with brand partners for campaign purposes:</p>
    <ul>
        <li>Your &#64;verticaltalent.co email address (not your personal contact information)</li>
        <li>Social media demographics and audience analytics</li>
        <li>Previous campaign performance data and metrics</li>
        <li>Media kits and promotional materials</li>
        <li>Campaign deliverables and results</li>
    </ul>
    <p>We do <strong>not</strong> share your personal phone number, home address, or SSN/EIN with brands.</p>

    <h3>6.2 With Service Providers</h3>
    <p>We use third-party service providers to support our operations, including:</p>
    <ul>
        <li><strong>Google Services:</strong> Google Workspace for email (&#64;verticaltalent.co accounts), Google Drive for file storage and sharing, and Google Forms for W-9 collection.</li>
        <li><strong>Social Media Platforms:</strong> TikTok, Instagram, and YouTube APIs (with your OAuth authorization) to collect analytics and performance data.</li>
        <li><strong>Payment Processors:</strong> Third-party services for processing creator payments.</li>
    </ul>
    <p>These service providers have their own privacy policies, and we encourage you to review them.</p>

    <h3>6.3 For Legal Requirements</h3>
    <p>We may disclose your information if required by law, court order, or government regulation, or to protect our legal rights.</p>

    <h2>7. How We Protect Your Information</h2>
    <p>We implement industry-standard security measures to safeguard your information, including:</p>
    <ul>
        <li>Secure server storage with encryption</li>
        <li>HTTPS/SSL encrypted connections</li>
        <li>Access controls and authentication requirements</li>
        <li>Regular security reviews and updates</li>
    </ul>
    <p>However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.</p>

    <h2>8. Data Retention</h2>
    <p>We retain information for different periods depending on its purpose:</p>
    <ul>
        <li><strong>Contact Form Submissions:</strong> Not stored in our database; only received via email.</li>
        <li><strong>Creator Information:</strong> We retain campaign records, including creator names, social media handles, and campaign data, indefinitely for business, legal, and financial record-keeping purposes. This includes records for creators who are no longer represented by us, as we must maintain accurate historical campaign information.</li>
        <li><strong>Tax Information:</strong> W-9 data and SSN/EIN information is retained as required by federal and state tax laws (typically 7 years).</li>
    </ul>
    <p>Even if you are no longer represented by Vertical Talent, we will retain records of campaigns you participated in, as these records are necessary for our business operations and legal compliance.</p>

    <h2>9. Your Privacy Rights</h2>
    <p>Depending on your location, you may have certain rights regarding your personal information:</p>

    <h3>9.1 All Users</h3>
    <ul>
        <li><strong>Access:</strong> Request information about the personal data we hold about you.</li>
        <li><strong>Correction:</strong> Request that we correct inaccurate or incomplete information.</li>
        <li><strong>Data Portability:</strong> Request a copy of your data in a structured, machine-readable format.</li>
    </ul>
    <p>If you would like to request deletion of creator data collected from social media platforms, submit your request at <a href="/delete-data">verticaltalentgroup.com/delete-data</a>.</p>

    <h3>9.2 GDPR Rights (European Economic Area)</h3>
    <p>If you are located in the EEA, you have additional rights under GDPR, including:</p>
    <ul>
        <li>The right to object to data processing</li>
        <li>The right to restrict processing</li>
        <li>The right to withdraw consent</li>
    </ul>

    <h3>9.3 CCPA Rights (California Residents)</h3>
    <p>If you are a California resident, you have rights under the California Consumer Privacy Act (CCPA), including:</p>
    <ul>
        <li>The right to know what personal information we collect and how it's used</li>
        <li>The right to request deletion of your personal information (subject to legal exceptions)</li>
        <li>The right to opt-out of the sale of personal information (note: we do not sell personal information)</li>
    </ul>

    <h3>9.4 Important Limitation</h3>
    <p>Please note that due to business and legal requirements, we cannot delete campaign records, including your name, social media handles, and campaign participation history, even upon request. However, we can remove or anonymize other personal information such as your current contact details where legally permissible.</p>

    <p>To exercise any of these rights, please contact us at <a href="mailto:info@verticaltalentgroup.com">info&#64;verticaltalentgroup.com</a>.</p>

    <h2>10. International Users</h2>
    <p>Our services are primarily based in the United States. If you access our Site or App from outside the United States, your information may be transferred to, stored, and processed in the United States. By using our services, you consent to the transfer of your information to the United States and acknowledge that U.S. data protection laws may differ from those in your country.</p>

    <h2>11. Children's Privacy</h2>
    <p>Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from anyone under 18 years of age. All creators we represent must be 18 years or older.</p>

    <h2>12. Changes to This Privacy Policy</h2>
    <p>We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. When we make changes, we will update the "Last Updated" date at the top of this page. We encourage you to review this Privacy Policy periodically. Your continued use of our Site or App after changes are posted constitutes your acceptance of the updated Privacy Policy.</p>

    <h2>13. Contact Us</h2>
    <p>If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:</p>
    <p>
        <strong>Vertical Talent, LLC</strong><br>
        Email: <a href="mailto:info@verticaltalentgroup.com">info&#64;verticaltalentgroup.com</a>
    </p>
  </div>
</div>
